// src/pages/mechanic/profile/MechanicKyc.jsx

import React, { useState, useRef, useContext, useEffect } from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import { MdSave, MdVerifiedUser, MdUpload } from "react-icons/md";
import { FiClock, FiCheckCircle, FiAlertCircle } from "react-icons/fi";
import "../../../assets/css/mechanic/kyc/KYC.css";
import MechanicAdminLayout from "../../../layout/mechanic/MechanicMasterLayout";
import Swal from "sweetalert2";
import AppContext from "../../../context/AppContext";
import axios from "axios";

const MechanicKyc = () => {
  // ── States ──
  const [kycStatus, setKycStatus] = useState("pending"); // "pending" | "submitted" | "approved" | "rejected"
  const [uploadedFile, setUploadedFile] = useState(null);
  const { baseUrl } = useContext(AppContext);
  const pdfInputRef = useRef(null);

  // ── Handlers ──
  const handlePdfUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/pdf") {
      setUploadedFile(file);
    } else {
      alert("Please upload a PDF file only.");
    }
  };

  const handleSubmitVerification = () => {
    console.log("Submitted KYC PDF:", uploadedFile);

    // const savedMechanic = JSON.parse(localStorage.getItem("LoggedInMechanic"));
     const token = localStorage.getItem("authToken");

    const formData = new FormData();
    formData.append("document", uploadedFile);
    // formData.append("id", savedMechanic.id);

    axios
      .post(`${baseUrl}/mechanicadmin/kyc/submitKyc`, formData, {
       headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        console.log("KYC submission response:", res.data);
        if(res.data.success === true) {
          setKycStatus("submitted");
          Swal.fire({
            icon: "success",
            title: "KYC Submitted",
            text: "Your KYC documents have been submitted successfully! We will review them within 2-3 business days.",
          });
        }
      })
      .catch(() => {
        Swal.fire({
          icon: "error",
          title: "Submission Failed",
          text: "There was an error submitting your KYC documents. Please try again.",
        });
      });

    // if (!uploadedFile) {
    //   alert("Please upload your KYC documents PDF first.");
    //   return;
    // }
    // setKycStatus("submitted");
    // alert(
    //   "KYC documents submitted successfully! We will review within 2-3 business days.",
    // );
  };

const getKycStatus = () => {
  const token = localStorage.getItem("authToken");

  axios
    .get(
      `${baseUrl}/mechanicadmin/profile/fetchMechanicProfile`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => {
      const mechanic = res.data.mechanic;

      if (mechanic.verification_status === "verified") {
        setKycStatus("approved");
      } 
      else if (mechanic.verification_status === "rejected") {
        setKycStatus("rejected");
      } 
      else if (mechanic.kyc_status === "submitted") {
        setKycStatus("submitted");
      } 
      else {
        setKycStatus("pending");
      }
    })
    .catch((err) => {
      console.log("KYC Status Error:", err);
    });
};

useEffect(() => {
  getKycStatus();
}, []);

  // ── Status Banner Config ──
  const statusConfig = {
    pending: {
      icon: <FiClock size={22} />,
      title: "Verification Pending",
      desc: "Please submit your documents to complete verification.",
      badge: "Pending Review",
      cls: "status-pending",
    },
    submitted: {
      icon: <FiClock size={22} />,
      title: "Documents Under Review",
      desc: "Your documents are under review. We will notify you once verified.",
      badge: "Under Review",
      cls: "status-submitted",
    },
    approved: {
      icon: <FiCheckCircle size={22} />,
      title: "Verification Approved",
      desc: "Your KYC has been successfully verified. You can now access all features.",
      badge: "Approved",
      cls: "status-approved",
    },
    rejected: {
      icon: <FiAlertCircle size={22} />,
      title: "Verification Rejected",
      desc: "Your documents were not accepted. Please re-upload correct documents.",
      badge: "Rejected",
      cls: "status-rejected",
    },
  };

  const currentStatus = statusConfig[kycStatus];

  // ================================================================
  // RETURN
  // ================================================================
  return (
    <MechanicAdminLayout>
      <Container fluid className="mechanic-kyc-page">
        {/* Page Header */}
        <div className="kyc-page-header">
          <div className="kyc-page-header-icon">
            <MdVerifiedUser size={28} />
          </div>
          <div>
            <h2>KYC Verification</h2>
            <p>
              Complete your identity verification to access all mechanic
              features
            </p>
          </div>
        </div>

        {/* ── Status Banner ── */}
        <div className={`kyc-status-banner ${currentStatus.cls}`}>
          <div className="kyc-status-left">
            <div className="kyc-status-icon">{currentStatus.icon}</div>
            <div>
              <h4>{currentStatus.title}</h4>
              <p>{currentStatus.desc}</p>
            </div>
          </div>
          <span className="kyc-status-badge">{currentStatus.badge}</span>
        </div>

        {/* ── Upload Instructions ── */}
        <div className="kyc-section-card">
          <h4 className="kyc-section-title">📋 Upload Instructions</h4>
          <Row>
            <Col md={6}>
              <div className="kyc-instructions-box">
                <p className="kyc-instructions-heading">
                  ✅ Required Documents:
                </p>
                <ul className="kyc-instructions-list">
                  <li>
                    <strong>CNIC Front Side</strong> — Clear &amp; readable
                  </li>
                  <li>
                    <strong>CNIC Back Side</strong> — Clear &amp; readable
                  </li>
                  <li>
                    <strong>Mechanic License / Certificate</strong> — Official
                    document
                  </li>
                  <li>
                    CNIC must be <strong>valid and not expired</strong>
                  </li>
                  <li>
                    All documents <strong>must be included</strong>
                  </li>
                </ul>
              </div>
            </Col>
            <Col md={6}>
              <div className="kyc-instructions-box">
                <p className="kyc-instructions-heading">
                  📎 File Specifications:
                </p>
                <ul className="kyc-instructions-list">
                  <li>
                    Format: <strong>PDF only</strong>
                  </li>
                  <li>
                    Maximum size: <strong>20 MB</strong>
                  </li>
                  <li>
                    Combine all pages into <strong>single PDF</strong>
                  </li>
                  <li>
                    Recommended: Scan at <strong>300 DPI</strong>
                  </li>
                  <li>Do not password protect the PDF</li>
                </ul>
              </div>
            </Col>
          </Row>
        </div>

        {/* ── PDF Upload Box ── */}
        <div className="kyc-section-card">
          <h4 className="kyc-section-title">
            KYC Verification (Single PDF File)
          </h4>
          <p className="kyc-upload-subtitle">
            Upload a single PDF file containing all required documents
          </p>

          <div
            className={`kyc-upload-box ${uploadedFile ? "has-file" : ""}`}
            onClick={() => pdfInputRef.current.click()}
          >
            <input
              type="file"
              accept="application/pdf"
              ref={pdfInputRef}
              onChange={handlePdfUpload}
              style={{ display: "none" }}
            />
            <div className="kyc-upload-icon">
              <MdUpload size={40} />
            </div>

            {uploadedFile ? (
              <>
                <p className="kyc-upload-title success-text">
                  ✅ {uploadedFile.name}
                </p>
                <p className="kyc-upload-size">
                  {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </>
            ) : (
              <>
                <p className="kyc-upload-title">Upload KYC Documents PDF</p>
                <p className="kyc-upload-size">Maximum File size: 20MB</p>
              </>
            )}

            <button
              className="kyc-choose-btn"
              onClick={(e) => {
                e.stopPropagation();
                pdfInputRef.current.click();
              }}
            >
              <MdUpload size={16} />
              {uploadedFile ? "Change PDF File" : "Choose PDF File"}
            </button>
          </div>
        </div>

        {/* ── Verification Process Steps ── */}
        <div className="kyc-section-card">
          <h4 className="kyc-section-title">⏱ Verification Process</h4>
          <div className="kyc-steps">
            {[
              {
                step: 1,
                title: "Upload Documents",
                desc: "Upload your PDF file with all required documents",
              },
              {
                step: 2,
                title: "Under Review",
                desc: "Our team will verify your documents (2–3 business days)",
              },
              {
                step: 3,
                title: "Verification Complete",
                desc: "You will receive email notification once approved",
              },
            ].map((s, i) => (
              <div key={i} className="kyc-step-item">
                <div className="kyc-step-number-col">
                  <div className="kyc-step-circle">{s.step}</div>
                  {i < 2 && <div className="kyc-step-line" />}
                </div>
                <div className="kyc-step-content">
                  <h5>{s.title}</h5>
                  <p>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Submit Button ── */}
        <div className="kyc-submit-wrapper">
          <button
            className="kyc-submit-btn"
            onClick={handleSubmitVerification}
            disabled={kycStatus === "submitted" || kycStatus === "approved"}
          >
            <MdSave size={18} />
            {kycStatus === "submitted"
              ? "Already Submitted"
              : kycStatus === "approved"
                ? "Verified ✓"
                : "Submit for Verification"}
          </button>
        </div>
      </Container>
    </MechanicAdminLayout>
  );
};

export default MechanicKyc;
